// Symptom-Risk Forecast — a science-grounded, day-level *pattern indicator*
// for peri/menopausal users. NOT an event predictor and NOT a diagnostic
// tool. Shared by the Today screen and the Symptom Intelligence Report so both surfaces
// always read from the same source of truth.
//
// Approach: for each overnight wearable metric, compare last night to that
// person's personal baseline (median of the prior N nights). Each metric
// contributes 1 point only if it deviates in the higher-symptom-risk
// direction. A multimodal score (>=2 signals) is required for anything
// above "Lower" — a single elevated signal (especially temperature alone)
// must never drive the forecast.

import type { WearableMetricRow } from "./wearableMetrics";

// ---------- Tunable constants ----------
export const BASELINE_WINDOW_NIGHTS = 14; // prior-night window for personal baseline
export const MIN_BASELINE_NIGHTS = 7;     // cold-start threshold

// Per-signal thresholds (deviation vs personal baseline, in the
// higher-symptom-risk direction).
export const TEMP_DELTA_C_THRESHOLD = 0.3;       // skin temp >= +0.3°C above baseline
export const HRV_DROP_PCT_THRESHOLD = 10;        // HRV >= 10% below baseline
export const RHR_RISE_BPM_THRESHOLD = 3;         // resting HR >= +3 bpm above baseline
export const SLEEP_DURATION_DROP_MIN_THRESHOLD = 45; // sleep >= 45 min below baseline
export const SLEEP_QUALITY_DROP_THRESHOLD = 10;  // sleep quality >= 10 pts below baseline
export const RESP_RATE_RISE_THRESHOLD = 1.0;    // respiratory rate >= +1.0 breaths/min above baseline

export type ForecastBand = "Lower" | "Moderate" | "Elevated";
export type ForecastStatus = "ok" | "building_baseline" | "no_data";

export type ContributingSignal = {
  key: "skin_temperature" | "hrv" | "resting_hr" | "respiratory_rate" | "sleep";
  label: string;
  lastNight: number;       // last-night value in native unit
  baseline: number;        // baseline value (median) in native unit
  delta: number;           // lastNight - baseline (sleep uses worse of duration/quality)
  unit: string;
  detail: string;          // short human-readable description, e.g. "+0.4°C vs your 14-night baseline"
};

export type SignalDirection = "higher" | "lower" | "near";

export type SignalSnapshot = {
  key: "skin_temperature" | "hrv" | "resting_hr" | "respiratory_rate" | "sleep";
  label: string;
  lastNight: number;
  baseline: number;
  delta: number;          // lastNight - baseline (in native unit; sleep uses duration min)
  unit: string;
  /** Direction relative to baseline interpreted as symptom-risk: */
  /** "higher" = worse direction (temp up, HRV down, RHR up, sleep down) */
  /** "lower" = better direction; "near" = within neutral band */
  riskDirection: SignalDirection;
  /** Plain-language summary, e.g. "6h 27m, close to your usual" */
  display: string;
};

export type SymptomRiskForecast = {
  status: ForecastStatus;
  band: ForecastBand | null;
  score: number;                       // 0–4
  contributingSignals: ContributingSignal[];
  /** Every measured signal with a baseline — used by the Today card to
   *  describe Lower / Moderate days truthfully (not just fired signals). */
  signalSnapshots: SignalSnapshot[];
  baselineNights: number;              // how many usable nights were available
  asOfDate: string | null;             // metric_date of "last night" row
};

// ---------- helpers ----------
function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const n = s.length;
  if (n === 0) return NaN;
  const mid = Math.floor(n / 2);
  return n % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function baselineFor(
  rows: WearableMetricRow[],
  field: keyof WearableMetricRow,
): { values: number[]; median: number | null } {
  const values: number[] = [];
  for (const r of rows) {
    const v = num(r[field] as unknown);
    if (v !== null) values.push(v);
  }
  return { values, median: values.length ? median(values) : null };
}

/**
 * Compute the forecast from a sequence of wearable_metrics rows ordered
 * ascending by metric_date. The most recent row is treated as "last night";
 * the prior BASELINE_WINDOW_NIGHTS rows form the personal baseline.
 */
export function computeSymptomRiskForecast(
  rows: WearableMetricRow[],
): SymptomRiskForecast {
  const empty: SymptomRiskForecast = {
    status: "no_data",
    band: null,
    score: 0,
    contributingSignals: [],
    signalSnapshots: [],
    baselineNights: 0,
    asOfDate: null,
  };
  if (!rows || rows.length === 0) return empty;

  const sorted = [...rows].sort((a, b) => a.metric_date.localeCompare(b.metric_date));
  const lastNight = sorted[sorted.length - 1];
  const priorAll = sorted.slice(0, -1);
  const priorWindow = priorAll.slice(Math.max(0, priorAll.length - BASELINE_WINDOW_NIGHTS));

  // Per-signal baseline counts (each metric may have a different usable count).
  const tempBase = baselineFor(priorWindow, "skin_temperature_c");
  const hrvBase = baselineFor(priorWindow, "hrv_ms");
  const rhrBase = baselineFor(priorWindow, "resting_heart_rate");
  const sleepDurBase = baselineFor(priorWindow, "sleep_duration_minutes");
  const sleepQualBase = baselineFor(priorWindow, "sleep_quality_score");
  const respBase = baselineFor(priorWindow, "respiratory_rate");

  // Cold-start: require MIN_BASELINE_NIGHTS of *any* signal before scoring.
  const maxBaselineN = Math.max(
    tempBase.values.length,
    hrvBase.values.length,
    rhrBase.values.length,
    sleepDurBase.values.length,
    sleepQualBase.values.length,
  );
  if (maxBaselineN < MIN_BASELINE_NIGHTS) {
    return {
      status: "building_baseline",
      band: null,
      score: 0,
      contributingSignals: [],
      signalSnapshots: [],
      baselineNights: maxBaselineN,
      asOfDate: lastNight.metric_date,
    };
  }

  const contributing: ContributingSignal[] = [];

  // 1) Skin temperature — intentionally NOT scored into the next-day outlook
  // band. Skin temperature is a same-night night-sweat signal, not a
  // next-day recovery predictor. It is still computed below for
  // signalSnapshots so the Today card's skin-temp tile and the separate
  // same-night night-sweat note keep working.
  const tempLast = num(lastNight.skin_temperature_c);
  // (no contributing.push for skin_temperature — recovery signals only)

  // 2) HRV (RMSSD) drop %
  const hrvLast = num(lastNight.hrv_ms);
  if (hrvLast !== null && hrvBase.median !== null && hrvBase.median > 0 && hrvBase.values.length >= MIN_BASELINE_NIGHTS) {
    const dropPct = ((hrvBase.median - hrvLast) / hrvBase.median) * 100;
    if (dropPct >= HRV_DROP_PCT_THRESHOLD) {
      contributing.push({
        key: "hrv",
        label: "HRV",
        lastNight: hrvLast,
        baseline: hrvBase.median,
        delta: hrvLast - hrvBase.median,
        unit: "ms",
        detail: `${Math.round(dropPct)}% below your usual HRV (${Math.round(hrvLast)} vs ${Math.round(hrvBase.median)} ms)`,
      });
    }
  }

  // 3) Resting heart rate rise
  const rhrLast = num(lastNight.resting_heart_rate);
  if (rhrLast !== null && rhrBase.median !== null && rhrBase.values.length >= MIN_BASELINE_NIGHTS) {
    const delta = rhrLast - rhrBase.median;
    if (delta >= RHR_RISE_BPM_THRESHOLD) {
      contributing.push({
        key: "resting_hr",
        label: "Resting heart rate",
        lastNight: rhrLast,
        baseline: rhrBase.median,
        delta,
        unit: "bpm",
        detail: `+${Math.round(delta)} bpm above your usual resting heart rate`,
      });
    }
  }

  // 4) Sleep: duration drop OR quality drop (either fires the single sleep signal)
  const sleepDurLast = num(lastNight.sleep_duration_minutes);
  const sleepQualLast = num(lastNight.sleep_quality_score);
  let sleepFired: ContributingSignal | null = null;
  if (
    sleepDurLast !== null &&
    sleepDurBase.median !== null &&
    sleepDurBase.values.length >= MIN_BASELINE_NIGHTS
  ) {
    const drop = sleepDurBase.median - sleepDurLast;
    if (drop >= SLEEP_DURATION_DROP_MIN_THRESHOLD) {
      sleepFired = {
        key: "sleep",
        label: "Sleep",
        lastNight: sleepDurLast,
        baseline: sleepDurBase.median,
        delta: sleepDurLast - sleepDurBase.median,
        unit: "min",
        detail: `${Math.round(drop)} min less sleep than your usual baseline`,
      };
    }
  }
  if (
    !sleepFired &&
    sleepQualLast !== null &&
    sleepQualBase.median !== null &&
    sleepQualBase.values.length >= MIN_BASELINE_NIGHTS
  ) {
    const drop = sleepQualBase.median - sleepQualLast;
    if (drop >= SLEEP_QUALITY_DROP_THRESHOLD) {
      sleepFired = {
        key: "sleep",
        label: "Sleep quality",
        lastNight: sleepQualLast,
        baseline: sleepQualBase.median,
        delta: sleepQualLast - sleepQualBase.median,
        unit: "/100",
        detail: `${Math.round(drop)} points below your usual sleep quality`,
      };
    }
  }
  if (sleepFired) contributing.push(sleepFired);

  const score = contributing.length; // 0–3 (recovery signals: sleep, HRV, RHR)

  // Banding — recovery signals only; hard rule: anything above "Lower"
  // requires >=2 signals. 0–1 = Lower, exactly 2 = Moderate, 3 = Elevated.
  let band: ForecastBand = "Lower";
  if (score >= 3) band = "Elevated";
  else if (score === 2) band = "Moderate";
  else band = "Lower"; // 0 or 1 — single signal cannot lift band

  // Build a snapshot of EVERY measured signal with a baseline, regardless of
  // whether it crossed the risk threshold. This lets the Today card describe
  // Lower / Moderate days truthfully ("your HRV is near your usual baseline")
  // instead of falling back to a generic line.
  const snapshots: SignalSnapshot[] = [];

  const fmtSleep = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins - h * 60);
    return `${h}h ${m.toString().padStart(2, "0")}m`;
  };

  const deltaProvided = num((lastNight as any).skin_temperature_delta_c);
  if (deltaProvided !== null) {
    const d = deltaProvided;
    const dir: SignalDirection = d >= TEMP_DELTA_C_THRESHOLD ? "higher" : d <= -TEMP_DELTA_C_THRESHOLD ? "lower" : "near";
    snapshots.push({
      key: "skin_temperature", label: "Skin temperature",
      lastNight: deltaProvided, baseline: 0, delta: d, unit: "°C",
      riskDirection: dir,
      display: (() => {
        // Show skin-temp delta in °F (delta = °C × 1.8, NO +32 offset) so the
        // Why? panel matches the rest of the user-facing UI.
        const f = d * 1.8;
        const fSign = f >= 0 ? "+" : "";
        if (dir === "near") return `${fSign}${f.toFixed(1)}°F — close to your baseline`;
        if (dir === "higher") return `${fSign}${f.toFixed(1)}°F above your baseline`;
        return `${f.toFixed(1)}°F below your baseline`;
      })(),
    });
  } else if (tempLast !== null && tempBase.median !== null && tempBase.values.length >= MIN_BASELINE_NIGHTS) {
    const d = tempLast - tempBase.median;
    const dir: SignalDirection = d >= TEMP_DELTA_C_THRESHOLD ? "higher" : d <= -TEMP_DELTA_C_THRESHOLD ? "lower" : "near";
    snapshots.push({
      key: "skin_temperature", label: "Skin temperature",
      lastNight: tempLast, baseline: tempBase.median, delta: d, unit: "°C",
      riskDirection: dir,
      display: (() => {
        // Show skin-temp delta in °F (delta = °C × 1.8, NO +32 offset) so the
        // Why? panel matches the rest of the user-facing UI.
        const f = d * 1.8;
        const fSign = f >= 0 ? "+" : "";
        if (dir === "near") return `${fSign}${f.toFixed(1)}°F — close to your baseline`;
        if (dir === "higher") return `${fSign}${f.toFixed(1)}°F above your baseline`;
        return `${f.toFixed(1)}°F below your baseline`;
      })(),
    });
  }

  const respLast = num(lastNight.respiratory_rate);

  if (respLast !== null && respBase.median !== null && respBase.values.length >= MIN_BASELINE_NIGHTS) {

    const d = respLast - respBase.median;

    const dir: SignalDirection = d >= RESP_RATE_RISE_THRESHOLD ? "higher" : d <= -RESP_RATE_RISE_THRESHOLD ? "lower" : "near";

    snapshots.push({

      key: "respiratory_rate",

      label: "Breathing rate",

      lastNight: respLast,

      baseline: respBase.median,

      delta: d,

      unit: "br/min",

      riskDirection: dir,

      display: (() => {

        const sign = d >= 0 ? "+" : "";

        if (dir === "near") return `${respLast.toFixed(1)} br/min — close to your baseline`;

        if (dir === "higher") return `${sign}${d.toFixed(1)} br/min above your baseline`;

        return `${d.toFixed(1)} br/min below your baseline`;

      })(),

    });

  }

  if (hrvLast !== null && hrvBase.median !== null && hrvBase.median > 0 && hrvBase.values.length >= MIN_BASELINE_NIGHTS) {
    const pct = ((hrvBase.median - hrvLast) / hrvBase.median) * 100;
    const dir: SignalDirection = pct >= HRV_DROP_PCT_THRESHOLD ? "higher" : pct <= -HRV_DROP_PCT_THRESHOLD ? "lower" : "near";
    snapshots.push({
      key: "hrv", label: "HRV",
      lastNight: hrvLast, baseline: hrvBase.median, delta: hrvLast - hrvBase.median, unit: "ms",
      riskDirection: dir,
      display: dir === "near"
        ? `${Math.round(hrvLast)} ms — close to your usual`
        : dir === "higher"
          ? `${Math.round(Math.abs(pct))}% below your usual (${Math.round(hrvLast)} ms)`
          : `${Math.round(Math.abs(pct))}% above your usual (${Math.round(hrvLast)} ms)`,
    });
  }
  if (rhrLast !== null && rhrBase.median !== null && rhrBase.values.length >= MIN_BASELINE_NIGHTS) {
    const d = rhrLast - rhrBase.median;
    const dir: SignalDirection = d >= RHR_RISE_BPM_THRESHOLD ? "higher" : d <= -RHR_RISE_BPM_THRESHOLD ? "lower" : "near";
    snapshots.push({
      key: "resting_hr", label: "Resting heart rate",
      lastNight: rhrLast, baseline: rhrBase.median, delta: d, unit: "bpm",
      riskDirection: dir,
      display: dir === "near"
        ? `${Math.round(rhrLast)} bpm — close to your usual`
        : dir === "higher"
          ? `+${Math.round(d)} bpm above your usual (${Math.round(rhrLast)} bpm)`
          : `${Math.round(d)} bpm below your usual (${Math.round(rhrLast)} bpm)`,
    });
  }
  if (sleepDurLast !== null && sleepDurBase.median !== null && sleepDurBase.values.length >= MIN_BASELINE_NIGHTS) {
    const drop = sleepDurBase.median - sleepDurLast;
    const dir: SignalDirection = drop >= SLEEP_DURATION_DROP_MIN_THRESHOLD ? "higher" : drop <= -SLEEP_DURATION_DROP_MIN_THRESHOLD ? "lower" : "near";
    snapshots.push({
      key: "sleep", label: "Sleep",
      lastNight: sleepDurLast, baseline: sleepDurBase.median, delta: sleepDurLast - sleepDurBase.median, unit: "min",
      riskDirection: dir,
      display: dir === "near"
        ? `${fmtSleep(sleepDurLast)} — close to your usual`
        : dir === "higher"
          ? `${fmtSleep(sleepDurLast)} (${Math.round(drop)} min less than usual)`
          : `${fmtSleep(sleepDurLast)} (${Math.round(-drop)} min more than usual)`,
    });
  }

  return {
    status: "ok",
    band,
    score,
    contributingSignals: contributing,
    signalSnapshots: snapshots,
    baselineNights: maxBaselineN,
    asOfDate: lastNight.metric_date,
  };
}

/**
 * Convenience: fetch + compute. Kept as a separate helper so server-side
 * code (Symptom Intelligence Report) can also call computeSymptomRiskForecast on rows it
 * already loaded without re-fetching.
 */
export async function loadSymptomRiskForecast(userId: string): Promise<SymptomRiskForecast> {
  const { fetchWearableMetrics } = await import("./wearableMetrics");
  // Pull baseline window + last night + a little slack for missing nights.
  const rows = await fetchWearableMetrics(userId, BASELINE_WINDOW_NIGHTS + 7);
  return computeSymptomRiskForecast(rows);
}

// ============================================================================
// Internal validation: live, real-data backtest of the existing forecast.
//
// For each candidate day D in the user's history, we:
//   1. Take all wearable_metrics rows with metric_date <= D,
//   2. Pass them through the existing computeSymptomRiskForecast (which
//      treats the last row as "last night" and the prior BASELINE_WINDOW_NIGHTS
//      rows as the personal baseline — recovery signals only, ≥2-signal gate),
//   3. Pair the resulting band/score with the mean daily symptom severity
//      logged for that same day D (from daily_logs).
//
// No look-ahead: the slice for day D contains only rows with metric_date <= D,
// and computeSymptomRiskForecast's baseline window is strictly prior to D.
//
// This is internal-only — never surfaced to end users. Used by the dev-only
// OutlookAccuracyPanel to honestly measure the existing heuristic against
// the user's own history before any clinical sign-off or personalized model.
// ============================================================================

const MIN_PAIRED_DAYS = 30;

export type OutlookAccuracyStatus = "insufficient_data" | "preliminary";

export type OutlookAccuracyResult = {
  status: OutlookAccuracyStatus;
  windowDays: number;
  pairedDays: number;
  byBand: Record<ForecastBand, { n: number; meanSeverity: number | null }>;
  /** Spearman ρ between forecast score (0–3) and daily mean severity. */
  spearman: number | null;
  /** AUC (Mann–Whitney U / n_pos·n_neg) for predicting top-tertile severity days. */
  auc: number | null;
  /** Diagnostic counts for the dev panel. */
  notes: {
    wearableRowsFetched: number;
    severityDaysFetched: number;
    skippedColdStart: number;
  };
};

function rankAverage(values: number[]): number[] {
  // Returns fractional ranks (average for ties), 1-based.
  const n = values.length;
  const idx = values.map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
  const ranks = new Array<number>(n);
  let i = 0;
  while (i < n) {
    let j = i;
    while (j + 1 < n && idx[j + 1].v === idx[i].v) j++;
    const avgRank = (i + j) / 2 + 1; // 1-based average
    for (let k = i; k <= j; k++) ranks[idx[k].i] = avgRank;
    i = j + 1;
  }
  return ranks;
}

function spearmanRho(xs: number[], ys: number[]): number | null {
  if (xs.length !== ys.length || xs.length < 3) return null;
  const rx = rankAverage(xs);
  const ry = rankAverage(ys);
  const n = xs.length;
  const mx = rx.reduce((s, v) => s + v, 0) / n;
  const my = ry.reduce((s, v) => s + v, 0) / n;
  let num = 0, dx = 0, dy = 0;
  for (let k = 0; k < n; k++) {
    const a = rx[k] - mx;
    const b = ry[k] - my;
    num += a * b;
    dx += a * a;
    dy += b * b;
  }
  const den = Math.sqrt(dx * dy);
  return den > 0 ? num / den : null;
}

function aucMannWhitney(scores: number[], labels: number[]): number | null {
  // labels: 1 = positive (top-tertile severity day), 0 = negative.
  let nPos = 0, nNeg = 0;
  for (const l of labels) { if (l === 1) nPos++; else if (l === 0) nNeg++; }
  if (nPos === 0 || nNeg === 0) return null;
  const ranks = rankAverage(scores);
  let sumPosRanks = 0;
  for (let i = 0; i < labels.length; i++) if (labels[i] === 1) sumPosRanks += ranks[i];
  const u = sumPosRanks - (nPos * (nPos + 1)) / 2;
  return u / (nPos * nNeg);
}

export async function computeOutlookAccuracy(
  userId: string,
  days = 90,
): Promise<OutlookAccuracyResult> {
  const { supabase } = await import("@/integrations/supabase/client");
  const { localDateKey, parseLocalDateKey } = await import("./dailyLogs");

  // We need enough rows BEFORE the earliest candidate day to form a baseline.
  // Fetch `days + BASELINE_WINDOW_NIGHTS + 7` of wearable rows; candidate days
  // are the most recent `days` (so each has prior nights available).
  const end = new Date();
  const fetchStart = new Date();
  fetchStart.setDate(fetchStart.getDate() - (days + BASELINE_WINDOW_NIGHTS + 7 - 1));
  const candidateStart = new Date();
  candidateStart.setDate(candidateStart.getDate() - (days - 1));

  const fetchStartKey = localDateKey(fetchStart);
  const endKey = localDateKey(end);
  const candidateStartKey = localDateKey(candidateStart);

  const [wearableRes, severityRes] = await Promise.all([
    supabase
      .from("wearable_metrics")
      .select(
        "metric_date, provider, sleep_duration_minutes, sleep_quality_score, hrv_ms, resting_heart_rate, skin_temperature_c, steps",
      )
      .eq("user_id", userId)
      .gte("metric_date", fetchStartKey)
      .lte("metric_date", endKey)
      .order("metric_date", { ascending: true }),
    supabase
      .from("daily_logs")
      .select("log_date, severity")
      .eq("user_id", userId)
      .gte("log_date", candidateStartKey)
      .lte("log_date", endKey),
  ]);

  const wearableRows = (wearableRes.data || []) as WearableMetricRow[];
  const severityRows = (severityRes.data || []) as Array<{ log_date: string; severity: number | null }>;

  // Mean severity per day.
  const sevSum = new Map<string, { sum: number; n: number }>();
  for (const r of severityRows) {
    if (typeof r.severity !== "number" || !Number.isFinite(r.severity)) continue;
    const cur = sevSum.get(r.log_date) || { sum: 0, n: 0 };
    cur.sum += r.severity; cur.n += 1;
    sevSum.set(r.log_date, cur);
  }
  const meanSevByDay = new Map<string, number>();
  for (const [k, v] of sevSum) meanSevByDay.set(k, v.sum / v.n);

  // Candidate days = each wearable row whose metric_date is in [candidateStart, end].
  const candidates = wearableRows.filter((r) => r.metric_date >= candidateStartKey);

  let skippedColdStart = 0;
  const paired: Array<{ band: ForecastBand; score: number; severity: number }> = [];

  for (const row of candidates) {
    const slice = wearableRows.filter((r) => r.metric_date <= row.metric_date);
    const f = computeSymptomRiskForecast(slice);
    if (f.status !== "ok" || f.band == null) { skippedColdStart++; continue; }
    const sev = meanSevByDay.get(row.metric_date);
    if (sev === undefined) continue;
    paired.push({ band: f.band, score: f.score, severity: sev });
  }

  const byBand: Record<ForecastBand, { n: number; meanSeverity: number | null }> = {
    Lower: { n: 0, meanSeverity: null },
    Moderate: { n: 0, meanSeverity: null },
    Elevated: { n: 0, meanSeverity: null },
  };
  const bandSums: Record<ForecastBand, { sum: number; n: number }> = {
    Lower: { sum: 0, n: 0 }, Moderate: { sum: 0, n: 0 }, Elevated: { sum: 0, n: 0 },
  };
  for (const p of paired) {
    bandSums[p.band].sum += p.severity;
    bandSums[p.band].n += 1;
  }
  (Object.keys(byBand) as ForecastBand[]).forEach((b) => {
    byBand[b].n = bandSums[b].n;
    byBand[b].meanSeverity = bandSums[b].n > 0 ? bandSums[b].sum / bandSums[b].n : null;
  });

  const notes = {
    wearableRowsFetched: wearableRows.length,
    severityDaysFetched: meanSevByDay.size,
    skippedColdStart,
  };

  const insufficient =
    paired.length < MIN_PAIRED_DAYS ||
    byBand.Lower.n === 0 ||
    byBand.Moderate.n === 0 ||
    byBand.Elevated.n === 0;

  if (insufficient) {
    return {
      status: "insufficient_data",
      windowDays: days,
      pairedDays: paired.length,
      byBand,
      spearman: null,
      auc: null,
      notes,
    };
  }

  const scores = paired.map((p) => p.score);
  const sevs = paired.map((p) => p.severity);
  const spearman = spearmanRho(scores, sevs);

  // Top-tertile severity = positive class.
  const sortedSev = [...sevs].sort((a, b) => a - b);
  const tertileIdx = Math.floor((sortedSev.length * 2) / 3);
  const tertileCut = sortedSev[tertileIdx];
  const labels = sevs.map((s) => (s >= tertileCut ? 1 : 0));
  const auc = aucMannWhitney(scores, labels);

  // Avoid "unused" warning for parseLocalDateKey in case future use needs it.
  void parseLocalDateKey;

  return {
    status: "preliminary",
    windowDays: days,
    pairedDays: paired.length,
    byBand,
    spearman,
    auc,
    notes,
  };
}

